import { DefaultError, QueryClient, QueryKey, useQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { corporateCMSApi } from "~/Services";

interface MainMenu {
  id: number;
  name: string;
  name_malay: string;
  name_mandarin: string;
  redirect_english: string;
  redirect_malay: string;
  redirect_mandarin: string;
  published: boolean | string;
  created_at: string;
  updated_at: string;
}

interface WebAppFooter {
  id: number;
  title: string;
  field_copyright_text_en: string;
  field_copyright_text_ms: string;
  field_copyright_text_zh: string;
  field_registration_number_txt_en: string;
  field_registration_number_txt_ms: string;
  field_registration_number_txt_zh: string;
  field_mobile_app_text_en: string;
  field_mobile_app_text_ms: string;
  field_mobile_app_text_zh: string;
  field_term_of_use_text_en: string;
  field_term_of_use_text_ms: string;
  field_term_of_use_text_zh: string;
  termsOfUseLink: string;
  field_privacy_policy_text_en: string;
  field_privacy_policy_text_ms: string;
  field_privacy_policy_text_zh: string;
  privacyPolicyLink: string;
  playStoreImage: string;
  playStoreLink: string;
  appStoreImage: string;
  appStoreLink: string;
  logos: {
    id: number;
    name: string;
    image: string;
    link: string;
    isSocialMedia: boolean;
    published: boolean;
    createdAt: string;
    updatedAt: string;
  }[];
  published: boolean;
  createdAt: string;
  updatedAt: string;
}

interface GrandChildMenus {
  id: number;
  name_en: string;
  name_ms: string;
  name_zh: string;
  redirect_url_en: string;
  redirect_url_ms: string;
  redirect_url_zh: string;
  parent_name: string;
  parent_id: number;
  published: boolean | string;
  created_at: string;
  updated_at: string;
}

interface ChildMenus {
  id: number;
  name_en: string;
  name_ms: string;
  name_zh: string;
  child_redirect_url_en: string;
  child_redirect_url_ms: string;
  child_redirect_url_zh: string;
  parent_name: string;
  parent_id: number;
  published: boolean | string;
  created_at: string;
  updated_at: string;
}
export interface WebAppFooterIcons {
  id: number;
  name: string;
  field_footer_logo_name_ms: string;
  field_footer_logo_name_zh: string;
  image: string;
  link: string;
  isSocialMedia: boolean;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}

const useMainMenu = (
  id: string,
  options?: UseQueryOptions<MainMenu, DefaultError, MainMenu, QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<MainMenu, DefaultError> => {
  return useQuery<MainMenu, DefaultError, MainMenu, QueryKey>(
    {
      queryKey: ["mainMenu", id],
      enabled: false,
      queryFn: async (): Promise<MainMenu> => {
        const response = await corporateCMSApi.get<any>(`/api/get-main-menu/allfields/${id}`);
        return response.data[0];
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useChildMenus = (
  id: string,
  options?: UseQueryOptions<ChildMenus, DefaultError, ChildMenus, QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<ChildMenus, DefaultError> => {
  return useQuery<ChildMenus, DefaultError, ChildMenus, QueryKey>(
    {
      queryKey: ["childMenus", id],
      enabled: false,
      queryFn: async (): Promise<ChildMenus> => {
        const response = await corporateCMSApi.get<any>(`/api/get-child-menu/allfields/${id}`);
        return response.data[0];
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useGrandChildMenus = (
  id: string,
  options?: UseQueryOptions<GrandChildMenus, DefaultError, GrandChildMenus, QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<GrandChildMenus, DefaultError> => {
  return useQuery<GrandChildMenus, DefaultError, GrandChildMenus, QueryKey>(
    {
      queryKey: ["grandChildHeader", id],
      enabled: false,
      queryFn: async (): Promise<GrandChildMenus> => {
        const response = await corporateCMSApi.get<any>(`/api/get-grand-child-menu/allfields/${id}`);
        return response.data[0];
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useWebAppFooter = (
  options?: UseQueryOptions<WebAppFooter, DefaultError, WebAppFooter, QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<WebAppFooter, DefaultError> => {
  return useQuery<WebAppFooter, DefaultError, WebAppFooter, QueryKey>(
    {
      queryKey: ["webAppFooter"],
      enabled: false,
      queryFn: async (): Promise<WebAppFooter> => {
        const response = await corporateCMSApi.get<any>(`/api/web-app-footer/allfields`);
        return response.data[0];
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useWebAppFooterIcons = (
  id: string,
  options?: UseQueryOptions<WebAppFooterIcons, DefaultError, WebAppFooterIcons, QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<WebAppFooterIcons, DefaultError> => {
  return useQuery<WebAppFooterIcons, DefaultError, WebAppFooterIcons, QueryKey>(
    {
      queryKey: ["webAppFooterIcons", id],
      enabled: false,
      queryFn: async (): Promise<WebAppFooterIcons> => {
        const response = await corporateCMSApi.get<any>(`/api/web-app-footer-logos/all-fields?id=${id}`);
        return response.data[0];
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

export { useChildMenus, useGrandChildMenus, useMainMenu, useWebAppFooter, useWebAppFooterIcons };
